import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import clsx from 'clsx'

// Provider options
const PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'grok', label: 'Grok (xAI)' },
  { value: 'deepseek', label: 'DeepSeek' },
]

/**
 * ProviderForm - Modal form for creating/editing translation provider configurations
 */
const ProviderForm = ({ provider, onSubmit, onClose }) => {
  const isEditing = Boolean(provider)

  const [formData, setFormData] = useState({
    name: '',
    provider: 'openai',
    is_active: true,
    max_rps: 10,
    batch_size: 20,
    temperature: 0.3,
    retry_attempts: 3,
    timeout_seconds: 120,
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Populate form when editing
  useEffect(() => {
    if (provider) {
      setFormData({
        name: provider.name || '',
        provider: provider.provider || 'openai',
        is_active: provider.is_active ?? true,
        max_rps: provider.max_rps || 10,
        batch_size: provider.batch_size || 20,
        temperature: provider.temperature ?? 0.3,
        retry_attempts: provider.retry_attempts || 3,
        timeout_seconds: provider.timeout_seconds || 120,
      })
    }
  }, [provider])

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  // Handle number input changes
  const handleNumberChange = (e) => {
    const { name, value } = e.target
    const numValue = parseFloat(value)
    setFormData(prev => ({
      ...prev,
      [name]: isNaN(numValue) ? '' : numValue
    }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  // Validate form
  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.provider) {
      newErrors.provider = 'Provider is required'
    }

    if (formData.max_rps < 1 || formData.max_rps > 1000) {
      newErrors.max_rps = 'RPS must be between 1 and 1000'
    }

    if (formData.batch_size < 1 || formData.batch_size > 100) {
      newErrors.batch_size = 'Batch size must be between 1 and 100'
    }

    if (formData.temperature < 0 || formData.temperature > 2) {
      newErrors.temperature = 'Temperature must be between 0 and 2'
    }

    if (formData.retry_attempts < 0 || formData.retry_attempts > 10) {
      newErrors.retry_attempts = 'Retry attempts must be between 0 and 10'
    }

    if (formData.timeout_seconds < 10 || formData.timeout_seconds > 600) {
      newErrors.timeout_seconds = 'Timeout must be between 10 and 600 seconds'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) return

    try {
      setIsSubmitting(true)
      await onSubmit({
        ...formData,
        max_rps: parseInt(formData.max_rps),
        batch_size: parseInt(formData.batch_size),
        temperature: parseFloat(formData.temperature),
        retry_attempts: parseInt(formData.retry_attempts),
        timeout_seconds: parseInt(formData.timeout_seconds),
      })
    } catch (err) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Provider' : 'Add Provider'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Configuration Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., OpenAI Production"
                  className={clsx(
                    'block w-full rounded-md shadow-sm text-sm',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'focus:ring-primary-500 focus:border-primary-500',
                    errors.name && 'border-red-500'
                  )}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
                )}
              </div>

              {/* Provider */}
              <div>
                <label
                  htmlFor="provider"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Provider *
                </label>
                <select
                  id="provider"
                  name="provider"
                  value={formData.provider}
                  onChange={handleChange}
                  className={clsx(
                    'block w-full rounded-md shadow-sm text-sm',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'focus:ring-primary-500 focus:border-primary-500',
                    errors.provider && 'border-red-500'
                  )}
                >
                  {PROVIDERS.map(p => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                {errors.provider && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.provider}</p>
                )}
              </div>

              {/* Rate Limit & Batch Size Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="max_rps"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Max RPS
                  </label>
                  <input
                    type="number"
                    id="max_rps"
                    name="max_rps"
                    value={formData.max_rps}
                    onChange={handleNumberChange}
                    min="1"
                    max="1000"
                    className={clsx(
                      'block w-full rounded-md shadow-sm text-sm',
                      'border-gray-300 dark:border-gray-600',
                      'bg-white dark:bg-gray-700',
                      'text-gray-900 dark:text-gray-100',
                      'focus:ring-primary-500 focus:border-primary-500',
                      errors.max_rps && 'border-red-500'
                    )}
                  />
                  {errors.max_rps && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.max_rps}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="batch_size"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Batch Size
                  </label>
                  <input
                    type="number"
                    id="batch_size"
                    name="batch_size"
                    value={formData.batch_size}
                    onChange={handleNumberChange}
                    min="1"
                    max="100"
                    className={clsx(
                      'block w-full rounded-md shadow-sm text-sm',
                      'border-gray-300 dark:border-gray-600',
                      'bg-white dark:bg-gray-700',
                      'text-gray-900 dark:text-gray-100',
                      'focus:ring-primary-500 focus:border-primary-500',
                      errors.batch_size && 'border-red-500'
                    )}
                  />
                  {errors.batch_size && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.batch_size}</p>
                  )}
                </div>
              </div>

              {/* Temperature & Retry Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="temperature"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Temperature
                  </label>
                  <input
                    type="number"
                    id="temperature"
                    name="temperature"
                    value={formData.temperature}
                    onChange={handleNumberChange}
                    min="0"
                    max="2"
                    step="0.1"
                    className={clsx(
                      'block w-full rounded-md shadow-sm text-sm',
                      'border-gray-300 dark:border-gray-600',
                      'bg-white dark:bg-gray-700',
                      'text-gray-900 dark:text-gray-100',
                      'focus:ring-primary-500 focus:border-primary-500',
                      errors.temperature && 'border-red-500'
                    )}
                  />
                  {errors.temperature && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.temperature}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="retry_attempts"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Retry Attempts
                  </label>
                  <input
                    type="number"
                    id="retry_attempts"
                    name="retry_attempts"
                    value={formData.retry_attempts}
                    onChange={handleNumberChange}
                    min="0"
                    max="10"
                    className={clsx(
                      'block w-full rounded-md shadow-sm text-sm',
                      'border-gray-300 dark:border-gray-600',
                      'bg-white dark:bg-gray-700',
                      'text-gray-900 dark:text-gray-100',
                      'focus:ring-primary-500 focus:border-primary-500',
                      errors.retry_attempts && 'border-red-500'
                    )}
                  />
                  {errors.retry_attempts && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.retry_attempts}</p>
                  )}
                </div>
              </div>

              {/* Timeout */}
              <div>
                <label
                  htmlFor="timeout_seconds"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Timeout (seconds)
                </label>
                <input
                  type="number"
                  id="timeout_seconds"
                  name="timeout_seconds"
                  value={formData.timeout_seconds}
                  onChange={handleNumberChange}
                  min="10"
                  max="600"
                  className={clsx(
                    'block w-full rounded-md shadow-sm text-sm',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'focus:ring-primary-500 focus:border-primary-500',
                    errors.timeout_seconds && 'border-red-500'
                  )}
                />
                {errors.timeout_seconds && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.timeout_seconds}</p>
                )}
              </div>

              {/* Active Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor="is_active"
                  className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                >
                  Active (available for translations)
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md',
                  'border border-gray-300 dark:border-gray-600',
                  'text-gray-700 dark:text-gray-300',
                  'bg-white dark:bg-gray-700',
                  'hover:bg-gray-50 dark:hover:bg-gray-600',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md text-white',
                  'bg-primary-600 hover:bg-primary-700',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  isEditing ? 'Update Provider' : 'Create Provider'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ProviderForm
